import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ShieldCheck, Key, Smartphone, Monitor, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const Security = () => {
  const [sessions] = useState([
    { id: 1, device: 'Chrome on Windows', location: 'Detroit, MI', lastActive: 'Now', current: true },
    { id: 2, device: 'Safari on iPhone', location: 'Detroit, MI', lastActive: '2 hours ago', current: false },
  ]);

  const handleChangePassword = () => {
    toast.info('Password change feature coming soon');
  };

  const handleEnable2FA = () => {
    toast.info('2FA setup coming soon');
  };

  const handleLogoutDevice = (sessionId) => {
    toast.success('Device logged out');
  };

  const handleLogoutAll = () => {
    if (window.confirm('Log out from all devices?')) {
      toast.success('Logged out from all devices');
    }
  };

  return (
    <div className="space-y-3">
      {/* Password Section */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-3">
            <Key className="w-3.5 h-3.5 text-blue-400" />
            <h3 className="text-xs font-semibold text-white">Password</h3>
          </div>
          <p className="text-[10px] text-gray-400 mb-3">Update your password to keep your account secure</p>
          
          <div className="flex items-center justify-between p-2 bg-gray-900 rounded">
            <div>
              <p className="text-xs text-gray-300">Password last changed</p>
              <p className="text-[10px] text-gray-500">30 days ago</p>
            </div>
            <Button 
              onClick={handleChangePassword}
              className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700"
            >
              Update Password
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-3">
            <Smartphone className="w-3.5 h-3.5 text-green-400" />
            <h3 className="text-xs font-semibold text-white">Two-Factor Authentication</h3>
          </div>
          <p className="text-[10px] text-gray-400 mb-3">Add an extra layer of security to your account</p>
          
          <div className="flex items-center justify-between p-2 bg-gray-900 rounded">
            <div>
              <p className="text-xs text-gray-300">Status</p>
              <p className="text-[10px] text-yellow-400">Not enabled</p>
            </div>
            <Button 
              onClick={handleEnable2FA}
              variant="outline"
              className="h-7 px-3 text-xs border-gray-600 text-gray-300 hover:text-white"
            >
              Enable 2FA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Monitor className="w-3.5 h-3.5 text-purple-400" />
              <h3 className="text-xs font-semibold text-white">Active Sessions</h3>
            </div>
            <Button 
              onClick={handleLogoutAll}
              variant="ghost"
              className="h-6 px-2 text-[10px] text-red-400 hover:text-red-300 hover:bg-red-900/20"
            >
              <LogOut className="w-3 h-3 mr-1" />
              Log Out All
            </Button>
          </div>
          <p className="text-[10px] text-gray-400 mb-3">Devices where you're currently logged in</p>
          
          <div className="space-y-2">
            {sessions.map(session => (
              <div key={session.id} className="flex items-center justify-between p-2 bg-gray-900 rounded">
                <div className="flex items-center gap-2">
                  <Monitor className="w-3.5 h-3.5 text-gray-500" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-300">{session.device}</p>
                      {session.current && (
                        <span className="px-1.5 py-0.5 text-[10px] bg-green-900/30 text-green-400 rounded">Current</span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-500">{session.location} • {session.lastActive}</p>
                  </div>
                </div>
                {!session.current && (
                  <Button 
                    onClick={() => handleLogoutDevice(session.id)}
                    variant="ghost"
                    className="h-6 px-2 text-[10px] text-gray-400 hover:text-white"
                  >
                    Log Out
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Security Log */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-3.5 h-3.5 text-orange-400" />
            <h3 className="text-xs font-semibold text-white">Recent Security Activity</h3>
          </div>
          <p className="text-[10px] text-gray-400 mb-3">Recent security events on your account</p>
          
          <div className="space-y-1">
            {[
              { event: 'Successful login', time: 'Just now', location: 'Detroit, MI' },
              { event: 'Successful login', time: '2 hours ago', location: 'Detroit, MI' },
              { event: 'Password changed', time: '30 days ago', location: 'Detroit, MI' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-1.5 border-b border-gray-700 last:border-0">
                <span className="text-xs text-gray-300">{item.event}</span>
                <span className="text-[10px] text-gray-500">{item.time} • {item.location}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Security;
